import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Spinner,
  useColorModeValue,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Divider,
  Badge,
} from "@chakra-ui/react";
import ProductApi from "../../api/product";
import axios from "../../api/index";
import DatePicker from "react-modern-calendar-datepicker";
import "react-modern-calendar-datepicker/lib/DatePicker.css";
import moment from "moment-jalaali";
import Select from "react-select";
import AsyncSelect from "react-select/async/dist/react-select-async.esm";

const convertJalaliToGregorian = (value) => {
  if (!value) return "";
  return moment(`${value.year}/${value.month}/${value.day}`, "jYYYY/jM/jD").format("YYYY-MM-DD");
};
// مقداردهی اولیه شمسی برای DatePicker
const getInitialJalali = (subtractMonths = 0) => {
  const m = moment().subtract(subtractMonths, 'months');
  // تبدیل به شمسی
  return {
    year: m.jYear(),
    month: m.jMonth() + 1,
    day: m.jDate(),
  };
};

const platformLabels = {
  ps4: "PS4",
  ps5: "PS5",
};
const groupLabels = {
  1: "ظرفیت ۱",
  2: "ظرفیت ۲",
  3: "ظرفیت ۳",
};

const ProductAnalytics = ({ initialProduct = null, fixedProduct = false, hideProductSelect = false, autoFetchOnMount = false }) => {
  // پیش‌فرض تاریخ خرید: ۶ ماه قبل تا امروز / فروش: ۳ ماه قبل تا امروز
  const [purchaseDateFrom, setPurchaseDateFrom] = useState(getInitialJalali(12));
  const [purchaseDateTo, setPurchaseDateTo] = useState(getInitialJalali(0));
  const [saleDateFrom, setSaleDateFrom] = useState(getInitialJalali(12));
  const [saleDateTo, setSaleDateTo] = useState(getInitialJalali(0));

  // مقدار اولیه برای productValue اگر initialProduct وجود دارد
  const [productValue, setProductValue] = useState(initialProduct ? { value: initialProduct.id, label: initialProduct.name } : null);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const textColor = useColorModeValue("gray.700", "white");

  useEffect(() => {
    if (autoFetchOnMount) {
      // تا رندر اولیه کامل شود سپس درخواست را بزنیم
      setTimeout(() => {
        fetchReport();
      }, 0);
    }
  }, [autoFetchOnMount]);

  // گرفتن لیست محصولات برای سرچ سلکت
  const loadProductOptions = async (inputValue, callback) => {
    try {
      const res = await ProductApi.Products(inputValue || "", 1);
      callback(
        (res.data.data || []).map((p) => ({ value: p.id, label: p.name }))
      );
    } catch {
      callback([]);
    }
  };

  // فراخوانی API گزارش (ارسال فقط پارامترهای غیر null)
  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      const pFrom = convertJalaliToGregorian(purchaseDateFrom);
      const pTo = convertJalaliToGregorian(purchaseDateTo);
      const sFrom = convertJalaliToGregorian(saleDateFrom);
      const sTo = convertJalaliToGregorian(saleDateTo);
      if (pFrom) params.purchase_date_from = pFrom;
      if (pTo) params.purchase_date_to = pTo;
      if (sFrom) params.sale_date_from = sFrom;
      if (sTo) params.sale_date_to = sTo;
      if (productValue && productValue.value) params.product_id = productValue.value;
      console.log('fetchReport params:', params); // لاگ برای کنترل ارسال شدن
      const res = await axios.get(`/reports/products`, { params });
      setReport(res.data.data || []);
    } catch (err) {
      setError("خطا در دریافت گزارش");
      console.error('fetchReport error:', err);
    } finally {
      setLoading(false);
    }
  };

  // توجه: خطای removeEventListener مربوط به DatePicker معمولاً تاثیر UXی ندارد و فقط در dev دیده می‌شود.

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Box mb={6} p={5} boxShadow="md" borderRadius="xl" bg={useColorModeValue("white", "gray.800")}>  
        <Flex wrap="wrap" gap={4} align="center">
          {!hideProductSelect && (
            <FormControl w={{ base: "100%", md: "250px" }}>
              <FormLabel fontSize="sm">انتخاب محصول</FormLabel>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadProductOptions}
                value={productValue}
                onChange={fixedProduct ? undefined : setProductValue}
                isClearable={!fixedProduct}
                placeholder={fixedProduct && productValue ? productValue.label : "جستجو و انتخاب محصول..."}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                isRtl={true}
                isDisabled={fixedProduct}
                menuIsOpen={fixedProduct ? false : undefined}
              />
            </FormControl>
          )}
          <FormControl w={{ base: "100%", md: "200px" }}>
            <FormLabel fontSize="sm">تاریخ خرید از</FormLabel>
            <DatePicker
              value={purchaseDateFrom}
              onChange={setPurchaseDateFrom}
              locale="fa"
              shouldCloseOnSelect={true}
              inputPlaceholder="انتخاب تاریخ"
              calendarClassName="custom-rmdp-calendar"
            />
          </FormControl>
          <FormControl w={{ base: "100%", md: "200px" }}>
            <FormLabel fontSize="sm">تاریخ خرید تا</FormLabel>
            <DatePicker
              value={purchaseDateTo}
              onChange={setPurchaseDateTo}
              locale="fa"
              shouldCloseOnSelect={true}
              inputPlaceholder="انتخاب تاریخ"
              calendarClassName="custom-rmdp-calendar"
            />
          </FormControl>
          <FormControl w={{ base: "100%", md: "200px" }}>
            <FormLabel fontSize="sm">تاریخ فروش از</FormLabel>
            <DatePicker
              value={saleDateFrom}
              onChange={setSaleDateFrom}
              locale="fa"
              shouldCloseOnSelect={true}
              inputPlaceholder="انتخاب تاریخ"
              calendarClassName="custom-rmdp-calendar"
            />
          </FormControl>
          <FormControl w={{ base: "100%", md: "200px" }}>
            <FormLabel fontSize="sm">تاریخ فروش تا</FormLabel>
            <DatePicker
              value={saleDateTo}
              onChange={setSaleDateTo}
              locale="fa"
              shouldCloseOnSelect={true}
              inputPlaceholder="انتخاب تاریخ"
              calendarClassName="custom-rmdp-calendar"
            />
          </FormControl>
          <Button colorScheme="teal" onClick={fetchReport} isLoading={loading}>
            دریافت گزارش
          </Button>
        </Flex>
      </Box>
      <Box px={2}>
        {loading && <Spinner size="lg" />}
        {error && <Text color="red.500">{error}</Text>}
        {!loading && !error && report.map((item) => (
          <Box key={item.product_id} mb={8} borderWidth={1} borderRadius="2xl" p={5} boxShadow="lg" bg={useColorModeValue('white', 'gray.800')}>
            <Text fontWeight="bold" fontSize="xl" mb={2} color={textColor}>{item.product_name}</Text>
            <Divider my={2} />
            {/* جدول ظرفیت */}
            <Table size="sm" variant="simple" colorScheme="gray">
              <Thead>
                <Tr>
                  <Th>پلتفرم</Th>
                  <Th>ظرفیت</Th>
                  <Th>تعداد خریداری شده</Th>
                  <Th>تعداد فروخته شده</Th>
                  <Th>درآمد</Th>
                  <Th>باقی مانده</Th>
                </Tr>
              </Thead>
              <Tbody>
                {["ps4","ps5"].map(platform =>
                  [1, 2, 3].map(group => {
                    const cap = (item.capacities||[]).find(
                      c => c.group_status === group && c.platform === platform
                    );
                    return (
                      <Tr key={platform + '-' + group} bg={cap?.purchases > 0 ? "gray.50" : "white"}>
                        <Td>{platformLabels[platform]}</Td>
                        <Td>{groupLabels[group]}</Td>
                        <Td>{cap?.purchases || 0}</Td>
                        <Td>{cap?.sales || 0}</Td>
                        <Td>{cap?.revenue?.toLocaleString() || 0}</Td>
                        <Td>{cap?.remaining || 0}</Td>
                      </Tr>
                    )
                  })
                )}
              </Tbody>
              <Tbody>
                <Tr bg="teal.50" fontWeight="bold">
                  <Td colSpan={2}>جمع کل</Td>
                  <Td>{item.totals?.purchases || 0}</Td>
                  <Td>{item.totals?.sales || 0}</Td>
                  <Td>{item.totals?.revenue?.toLocaleString() || 0}</Td>
                  <Td>{item.totals?.remaining || 0}</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        ))}
        {!loading && !error && report.length === 0 && (
          <Text color="gray.400">داده‌ای برای نمایش وجود ندارد.</Text>
        )}
      </Box>
    </Flex>
  );
};

export default ProductAnalytics;
